import {
  Autocomplete,
  type AutocompleteProps,
  Avatar,
  CircularProgress,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import {
  type TextFieldProps as MuiTextFieldProps,
  TextField,
} from '@side/pantry';
import { type SyntheticEvent, useMemo, useState } from 'react';
import { areAgentsEquivalent } from '@/common/utils/agents';
import { formatTeamMemberIds } from '@/common/utils/formatTeamMemberId';
import { ContactSuggestionsNoResults } from '@/components/ContactSuggestionsNoResults';
import { useAgentAutocompleteOptions } from './AgentAutocomplete.helpers';
import type { AgentSelectOption } from './types';

export interface AgentAutocompleteProps
  extends Omit<
    AutocompleteProps<AgentSelectOption, false, boolean, false>,
    'onInputChange' | 'options' | 'renderInput'
  > {
  selectedAgent?: AgentSelectOption;
  TextFieldProps: MuiTextFieldProps;
  /** Filter DSL clauses for the identity `q` param. Build with `useAgentDslFilters`. */
  filters?: string[];
  /**
   * Array of agent IDs (formatted as "userId:teamId") to exclude from the dropdown options.
   * Useful for preventing duplicate agent selection across multiple autocomplete fields.
   */
  excludeAgentIds?: string[];
}

export const DEFAULT_VALUE: AgentSelectOption = {
  avatar: '',
  displayName: '',
  email: '',
  id: '',
  teamId: '',
  userIdentityId: '',
  teamIdentityId: '',
  firstName: '',
  lastName: '',
  licenseState: '',
};

/**
 * A autocomplete component that renders a list of agents that the current user has
 * transaction sharing permissions with.
 */
export function AgentAutocomplete({
  selectedAgent,
  onChange,
  TextFieldProps,
  filters,
  excludeAgentIds = [],
  ...rest
}: AgentAutocompleteProps) {
  const [search, setSearch] = useState('');
  const { loading, agents } = useAgentAutocompleteOptions({
    search,
    filters,
  });

  const handleInputChange = (_: SyntheticEvent, value: string) => {
    setSearch(value);
  };

  const handleChange: AgentAutocompleteProps['onChange'] = (
    event,
    value,
    reason,
    details,
  ) => {
    onChange?.(event, value, reason, details);
    setSearch('');
  };

  // Filter out excluded agents
  const filteredAgents = useMemo(() => {
    if (!excludeAgentIds.length) return agents;

    return agents.filter(
      (agent) =>
        !formatTeamMemberIds(agent).some((teamMemberId) =>
          excludeAgentIds.includes(teamMemberId),
        ),
    );
  }, [agents, excludeAgentIds]);

  // Ensure selected option always exists to avoid console warnings
  const optionsWithSelected = useMemo(() => {
    if (!selectedAgent?.id) return filteredAgents;

    const isSelectedInOptions = filteredAgents.some((option) =>
      areAgentsEquivalent(option, selectedAgent),
    );
    if (isSelectedInOptions) {
      return filteredAgents;
    }

    return [...filteredAgents, selectedAgent].sort((a, b) =>
      a.displayName.localeCompare(b.displayName),
    );
  }, [filteredAgents, selectedAgent]);

  return (
    <Autocomplete
      onChange={handleChange}
      options={optionsWithSelected}
      inputValue={search}
      value={selectedAgent ?? DEFAULT_VALUE}
      disableClearable={!search}
      forcePopupIcon={false}
      renderInput={(params) => (
        <TextField
          {...params}
          {...TextFieldProps}
          helperText={TextFieldProps.helperText ?? ' '}
          fixedHeight
          slotProps={{
            ...params.slotProps,
            ...TextFieldProps?.slotProps,
            input: {
              ...params.slotProps.input,
              ...TextFieldProps?.slotProps?.input,
              endAdornment: loading ? (
                <CircularProgress sx={{ color: 'inherit' }} size={20} />
              ) : (
                params.slotProps.input.endAdornment
              ),
            },
          }}
        />
      )}
      renderOption={({ key, ...optionProps }, agent) => (
        <li key={key} {...optionProps}>
          <ListItemAvatar>
            <Avatar alt="User avatar" src={agent.avatar ?? ''} />
          </ListItemAvatar>

          <ListItemText primary={agent.displayName} secondary={agent.email} />
        </li>
      )}
      autoComplete
      fullWidth
      getOptionLabel={(option) => option.displayName}
      isOptionEqualToValue={areAgentsEquivalent}
      noOptionsText={
        loading || !search ? (
          ''
        ) : (
          <ContactSuggestionsNoResults sx={{ pl: 0, pt: 1, pr: 0, pb: 1 }} />
        )
      }
      onInputChange={handleInputChange}
      slotProps={{
        popper: {
          sx: {
            '.MuiAutocomplete-noOptions:empty': {
              display: 'none',
            },
          },
        },
      }}
      {...rest}
    />
  );
}
